import { EMPTY, of } from 'rxjs';

import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { ConfirmationOrder } from '@models/confirmation-order';
import { GENERAL_CODE_CALLBACKS, TradeInfo } from '@models/trade-info.class';
import { Store } from '@ngrx/store';

import { OperationError } from '@models/operationError.class';
import { catchError } from 'rxjs/operators';
import {
  ALGORITHM_TYPE,
  AppState,
  GEOLOCATION_NOTIFICATION_MESSAGE,
  OPERATION_MODAL_OPENED,
  OPERATION_RESET,
  ORDER_TYPE,
  ORDER_TYPE_LIST,
  TRACKING_EVENTS,
  USER_ROLES,
  VIGENCIA,
} from 'src/app';
import {
  NEW_RELIC_KNOWN_ERRORS,
  NewRelicLogger,
} from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { NotificationService, OperationService, PatronService } from 'src/core';
import {
  AnalyticsTrackingService,
  TrackingOrderType,
} from 'src/core/services/analyticsTracking.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'stoplimitado-order',
  templateUrl: './stopLimitado.component.html',
  styleUrls: ['./stopLimitado.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StopLimitadoOrderComponent implements OnChanges {
  @Input() selectedPrice: number;
  @Input() currentContract: any;
  @Input() currentIssue: any;
  @Input() isReadAndWrite: boolean;
  @Input() hasIssue: boolean;
  @Input() replaceTitles: number;
  @Input() isInternalUser: boolean;
  @Input() userRole: any;
  @Input() isUserGlobal: boolean;
  @Input() mfaType: number;
  @Input() readToken: string;
  @Input() elPatronData: any;
  @Input() isDiscretional: any;
  @Input() isGeolocationEnabled: boolean;
  @Input() isPositionMocked: boolean;
  @Input() shouldStartLoaderOnOperationAttempt: boolean;
  @Input() shouldNotifyGeolocationOnOperationAttempt: boolean;

  public confirmationOrder: ConfirmationOrder;

  public isBuyButtonLoading: boolean;
  public isSellButtonLoading: boolean;
  private operationCallback: () => void;
  private bitBuy: any;
  public titles = 0;
  private duration = 1;
  private stopPrice = 0;
  private limitPrice = 0;
  private order: any = {};

  public vigencia = VIGENCIA;

  private saleType: string;
  public showConfirmation = false;
  private lockConfirmation = false;

  private token: string;

  public defaultDuration = { id: 1, name: '1 día' };

  public canOperate = true;
  public canModifyDuration = true;
  private lockCancel = false;

  private tradeInfo: TradeInfo;

  constructor(
    private el: ElementRef,
    private notificationService: NotificationService,
    private operationService: OperationService,
    private store: Store<AppState>,
    private patronService: PatronService,
    private analyticsTrackingService: AnalyticsTrackingService,
    private newRelicLogger: NewRelicLogger,
  ) {}

  reset() {
    this.titles = 0;
    this.duration = 0;
    this.stopPrice = 0;
    this.limitPrice = 0;
    this.order = {};
    this.saleType = '';
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';
    this.replaceTitles = 0;

    this.defaultDuration = { id: 1, name: '1 día' };

    this.store.dispatch({
      type: OPERATION_RESET,
      payload: { reset: true },
    });

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  changeToken(event: any) {
    if (event.length > 6) {
      this.lockConfirmation = true;
      return false;
    } else if (event.length === 6) {
      this.lockConfirmation = false;
    } else {
      this.lockConfirmation = true;
      this.token = event;
    }
  }

  setTitles(event: any) {
    this.titles = event;
    this.isValidTitles();
    this.isOrderReady(true);
  }

  setStopPrice(event: any) {
    const price = Number(event);
    const decimalsToFix = price < 1 ? 3 : 2;
    this.stopPrice = Number(price.toFixed(decimalsToFix));
    this.isValidPrice(false, this.stopPrice, '.prc-stop');
    this.isOrderReady(true);
  }

  setDuration(event: any) {
    this.duration = event.id;

    if (!this.canModifyDuration) {
      this.duration = 1;
    }

    this.isValidDuration();
    this.isOrderReady(true);
  }

  setLimitPrice(event: any) {
    const price = Number(event);
    const decimalsToFix = price < 1 ? 3 : 2;
    this.limitPrice = Number(price.toFixed(decimalsToFix));
    this.isValidPrice(false, this.limitPrice, '.prc-limit');
    this.isOrderReady(true);
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (
      changes.shouldStartLoaderOnOperationAttempt &&
      !changes.shouldStartLoaderOnOperationAttempt.currentValue &&
      this.operationCallback
    ) {
      /**
       * TODO: This doesn't work yet, due to a bug that re-renders the
       * entire operation puppet after fetching the geolocation, and
       * this wipes out the state of the puppet, so even if
       * the callback gets called, all the variables get their default
       * value right after, and the previous changes are never rendered.
       *
       * This should be replicated on every OrderType once this bug
       * gets fixed.
       */
      this.operationCallback();
      return;
    }

    const { userRole, isUserGlobal } = changes;

    if (userRole !== undefined && userRole.currentValue !== undefined) {
      this.canOperate = !this.isInternalUser;
      this.canOperate =
        userRole.currentValue === USER_ROLES.READONLY ? false : this.canOperate;
    }

    if (
      userRole !== undefined &&
      userRole.currentValue !== undefined &&
      isUserGlobal !== undefined &&
      isUserGlobal.currentValue !== undefined
    ) {
      this.canModifyDuration =
        userRole.currentValue === USER_ROLES.ADVISOR &&
        isUserGlobal.currentValue
          ? false
          : true;
    }

    if (changes.selectedPrice !== undefined) {
      if (changes.selectedPrice.currentValue !== undefined) {
        this.setStopPrice(changes.selectedPrice.currentValue);
      }
    }

    if (changes.currentIssue !== undefined) {
      if (changes.currentIssue.currentValue !== undefined) {
        this.isOrderReady(true);
      }
    }

    if (changes.replaceTitles !== undefined) {
      if (changes.replaceTitles.currentValue !== undefined) {
        if (changes.replaceTitles.currentValue > 0) {
          this.titles = changes.replaceTitles.currentValue;
        }
      }
    }
  }

  isValidTitles(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.titles <= 0 || isNaN(this.titles)) {
      // if (!onlyCheck) {
      //   parent.querySelector('.order-titles').classList.add('invalid');
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   parent.querySelector('.order-titles').classList.remove('invalid');
      // }
      return true;
    }
  }

  isValidPrice(onlyCheck: boolean = false, price: number, searchClass: string) {
    const parent = this.el.nativeElement;
    if (price <= 0 || isNaN(price)) {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.add('invalid');
      //   }
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.remove('invalid');
      //   }
      // }
      return true;
    }
  }

  isValidDuration(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.duration <= 0) {
      // if (!onlyCheck && parent.querySelector('.duration input') !== null) {
      //   parent.querySelector('.duration input').classList.add('invalid');
      // }
      return false;
    } else {
      // if (!onlyCheck && parent.querySelector('.duration input') !== null) {
      //   parent.querySelector('.duration input').classList.remove('invalid');
      // }
      return true;
    }
  }

  isValidIssue() {
    if (this.currentIssue !== undefined) {
      if (this.currentIssue.issueID !== undefined) {
        return true;
      }
    }
    return false;
  }

  isOrderReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;

    if (!this.canOperate) {
      return;
    }

    if (
      !this.isValidTitles(onlyCheck) ||
      !this.isValidDuration(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.stopPrice, '.prc-stop') ||
      !this.isValidPrice(onlyCheck, this.limitPrice, '.prc-limit') ||
      !this.isValidIssue()
    ) {
      if (
        parent.querySelector('.btn-compra') !== null &&
        parent.querySelector('.btn-venta') !== null
      ) {
        parent.querySelector('.btn-compra').classList.remove('active');
        parent.querySelector('.btn-venta').classList.remove('active');
      }
      return false;
    } else {
      if (parent.querySelector('.btn-compra') !== null) {
        parent.querySelector('.btn-compra').classList.add('active');
      }

      if (this.hasIssue || this.isPositionMocked) {
        parent.querySelector('.btn-venta').classList.add('active');
      }
      return true;
    }
  }

  sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  buildOrder(orderType: string) {
    const CAPITAL_ORDER: number =
      orderType === 'venta' ? ORDER_TYPE.VENTANORMAL : ORDER_TYPE.COMPRANORMAL;

    this.order = {
      contractId: this.currentContract,
      duration: this.duration,
      algoTradingTypeId: ALGORITHM_TYPE.STOPLIMIT,
      orders: [
        {
          issueId: new ChangeCharacterFromIssue().ChangeToAmpersand(
            this.currentIssue.issueID,
          ),
          instrumentType: this.currentIssue.instrumentType,
          quantity: this.titles,
          stop: this.stopPrice,
          price: this.limitPrice,
          algoTradingTypeId: ALGORITHM_TYPE.STOPLIMIT,
          capitalOrderTypeId: CAPITAL_ORDER,
        },
      ],
    };
  }

  tryBuy() {
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.buildOrder('compra');
        this.saleType = 'compra';
        this.bitBuy = true;
        this._getChallenge();
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = true;
        // this.isSellButtonLoading = false;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
  }

  trySell() {
    if (!this.hasIssue && !this.isPositionMocked) {
      return false;
    }
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.buildOrder('venta');
        this.saleType = 'venta';
        this.bitBuy = false;
        this._getChallenge();
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = false;
        // this.isSellButtonLoading = true;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
  }

  setShowConfirmation(toShow: boolean = false) {
    this.showConfirmation = toShow;
    this.lockConfirmation = this.isReadAndWrite ? false : true;

    if (!this.showConfirmation) {
      this.token = '';
    } else {
      this.confirmationOrder = {
        issueID: this.order.orders[0].issueId,
        issueName: this.currentIssue.issueName,
        stopPrice: this.order.orders[0].stop,
        limitPrice: this.order.orders[0].price,
        titles: this.order.orders[0].quantity,
        vigency: this.order.duration,
        type: 'Stop Limitado',
        isBuy: this.bitBuy,
        typeId: ORDER_TYPE_LIST.stopLimit,
      };

      this.tradeInfo = new TradeInfo(
        false,
        this.currentIssue,
        TrackingOrderType.LimitedStop,
        window.location.pathname,
        this.titles.toString(),
        this.saleType,
        (this.limitPrice * this.titles).toString(),
        this.limitPrice.toString(),
        this.order.duration,
      );

      const startTradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

      this.analyticsTrackingService.logEvent(
        TRACKING_EVENTS.start_trade.event,
        startTradeInfoData,
      );
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelConfirmation() {
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  registerOrder() {
    const startTime: any = new Date();
    const hash: any = this.newRelicLogger.buildHash(
      startTime.getTime(),
      this.order.contractId,
      this.currentIssue.issueID,
      this.titles,
      this.order.orders[0].capitalOrderTypeId,
    );

    this.order.orders[0].hash = hash;

    this.operationService
      .registerCapitalOrder(this.order)
      .pipe(
        catchError((error: OperationError) => {
          const customAttributes = {
            _type: 'StopLimitado',
            contractId: this.currentContract,
          };

          this.newRelicLogger.setEvent(
            NEW_RELIC_KNOWN_ERRORS.tradeError,
            error.toJSON(),
            customAttributes,
          );

          this.reset();

          this.tradeInfo.setSuccess(false);
          this.tradeInfo.setCallback(error.message);
          this.tradeInfo.setContractId(this.currentContract);

          const tradeErrorInfoData = this.tradeInfo.getAnaliticsTradeInfo();
          this.tradeInfo.setContractId('');

          this.analyticsTrackingService.logEvent(
            TRACKING_EVENTS.trade.event,
            tradeErrorInfoData,
          );
          return EMPTY;
        }),
      )
      .subscribe((value: any) => {
        const orderId: number =
          value.electronicOrderId !== undefined && value.electronicOrderId > 0
            ? value.electronicOrderId
            : value.vigenciaId;

        this.reset();
        this.notificationService.showSuccess({
          title: 'Orden Ejecutada',
          message: `La orden ${orderId} fue enviada correctamente.`,
          eventId: undefined,
          isBusinessError: false,
        });
        this.store.dispatch({
          type: OPERATION_RESET,
          payload: { reset: true },
        });

        this.tradeInfo.setSuccess(true);
        this.tradeInfo.setCallback(GENERAL_CODE_CALLBACKS.SUCCESS);
        const tradeSuccessInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          tradeSuccessInfoData,
        );
      });
  }

  private _getChallenge() {
    if (this.isReadAndWrite) {
      this.setShowConfirmation(true);
    } else {
      this.patronService
        .getChallenge()
        .pipe(
          catchError((error: any) => {
            this.sendOperativeMessage('Error', error.message);

            this.lockConfirmation = false;
            this.lockCancel = false;
            return of(error);
          }),
        )
        .subscribe(() => this.setShowConfirmation(true));
    }
  }
}
